/* ─────────────────────────────────────────────────────────────
   MonthDayFieldRenderer — Two selects for MM-DD format.
   Serialises to "MM-DD" (zero-padded month).
   Deserialises an existing "MM-DD" value back into the two selects.
   ───────────────────────────────────────────────────────────── */

import type { FieldRendererProps, FieldDisplayProps } from '../../config/types';
import { FilterSelect } from '@/components/ui/Select';
import { Text } from '@/components/ui/Text';

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const DAYS = Array.from({ length: 31 }, (_, i) => {
  const d = String(i + 1);
  return { value: d, label: d };
});

function parseMonthDay(value: string | null | undefined): { month: string; day: string } {
  if (!value) return { month: '', day: '' };
  const parts = value.split('-');
  if (parts.length === 2) {
    return { month: parts[0], day: String(parseInt(parts[1], 10)) };
  }
  return { month: '', day: '' };
}

export function MonthDayFieldRenderer({ field, value, onChange, error }: FieldRendererProps) {
  const { month, day } = parseMonthDay(value as string | null | undefined);

  const handleMonthChange = (newMonth: string) => {
    const m = newMonth === 'All' ? '' : newMonth;
    if (m && day) {
      onChange(`${m}-${day.padStart(2, '0')}`);
    } else {
      onChange(m ? `${m}-` : '');
    }
  };

  const handleDayChange = (newDay: string) => {
    const d = newDay === 'All' ? '' : newDay;
    if (month && d) {
      onChange(`${month}-${d.padStart(2, '0')}`);
    } else {
      onChange(d ? `-${d.padStart(2, '0')}` : '');
    }
  };

  return (
    <div className="flex gap-2">
      <div className="flex-1">
        <FilterSelect
          id={`${field.id}-month`}
          value={month || 'All'}
          onValueChange={handleMonthChange}
          options={MONTHS}
          placeholder="Month"
          variant="form"
          disabled={field.isDisabled}
        />
      </div>
      <div className="w-24">
        <FilterSelect
          id={`${field.id}-day`}
          value={day || 'All'}
          onValueChange={handleDayChange}
          options={DAYS}
          placeholder="Day"
          variant="form"
          disabled={field.isDisabled}
        />
      </div>
      {error && (
        <Text variant="body-sm" color="danger" className="mt-1">{error}</Text>
      )}
    </div>
  );
}

/* ── Display component for Review / read-only mode ── */

export function MonthDayDisplay({ value }: FieldDisplayProps) {
  const str = value as string | null | undefined;
  if (!str) return <Text variant="body-sm" weight="medium" as="span">—</Text>;

  const parts = str.split('-');
  if (parts.length === 2) {
    const monthNum = parseInt(parts[0], 10);
    const dayNum = parseInt(parts[1], 10);
    if (!isNaN(monthNum) && !isNaN(dayNum)) {
      let displayStr = str;
      try {
        // Use a fixed year (leap year 2000) so Feb 29 works
        const date = new Date(2000, monthNum - 1, dayNum);
        const monthName = date.toLocaleDateString('en-GB', { month: 'long' });
        displayStr = `${dayNum} ${monthName}`;
      } catch {
        // fall through — displayStr remains str
      }
      return <Text variant="body-sm" weight="medium" as="span">{displayStr}</Text>;
    }
  }
  return <Text variant="body-sm" weight="medium" as="span">{str}</Text>;
}
